import type { ReactNode } from 'react';
import { FadeUp } from '../primitives/FadeUp';
import { SectionH2 } from '../primitives/SectionH2';
import { SectionLabel } from '../primitives/SectionLabel';
import { SchedulerCard } from '../primitives/SchedulerCard';
import { ShufflerCard } from '../primitives/ShufflerCard';
import { TypewriterCard } from '../primitives/TypewriterCard';
import { spotlightGradientStyle } from '../primitives/SpotlightBox';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useSpotlight } from '../../hooks/useSpotlight';
import {
  ACCENT,
  BORDER,
  EASE,
  FONT_DISPLAY,
  FONT_MONO,
  MUTED,
  SURFACE,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
} from '../../tokens';

/** The oversized "02 — The Solution" card that opens the section. */
function SolutionSpotlightCard() {
  const isMobile = useIsMobile();
  const { containerRef, spotRef, hovered, handlers } = useSpotlight<HTMLDivElement>();

  return (
    <div
      ref={containerRef}
      {...handlers}
      style={{
        marginBottom: 48,
        background: SURFACE,
        border: `1px solid ${hovered ? 'rgba(255,107,0,0.35)' : BORDER}`,
        borderLeft: `3px solid ${ACCENT}`,
        borderRadius: 24,
        padding: isMobile ? 28 : 48,
        position: 'relative',
        overflow: 'hidden',
        transition: 'border-color 0.3s',
      }}
    >
      <div ref={spotRef} aria-hidden="true" style={spotlightGradientStyle(hovered)} />

      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ maxWidth: 800 }}>
          <SectionLabel number="02">The Solution</SectionLabel>
          <h3
            style={{
              fontFamily: FONT_DISPLAY,
              fontSize: 'clamp(2.4rem,4vw,3.2rem)',
              fontWeight: 400,
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              marginBottom: 16,
              color: TEXT_PRIMARY,
              lineHeight: 1.05,
            }}
          >
            Two Pillars. One System. Your Voice Throughout.
          </h3>
          <p style={{ color: MUTED, fontSize: '1.15rem', lineHeight: 1.7 }}>
            Brand Foundations answers the objections your customers already hold before they ever
            reach out, and Visibility and Volume makes sure they actually find you, through consistent
            posting, content built around what they already ask on calls, and a loop that learns
            what worked.
          </p>
        </div>
        <ShufflerCard />
      </div>
    </div>
  );
}

interface DemoCard {
  label: string;
  title: string;
  body: string;
  card: ReactNode;
}

const DEMO_CARDS: readonly DemoCard[] = [
  {
    label: 'The Pre-Sold OS',
    title: 'It Writes From Your Calls, Not From Nothing.',
    body: 'The system mines your own sales calls and email chains for the objections that keep coming up, then drafts hook, reframe, proof and CTA against the ones that come up most. Grounded in your business, checked by a human.',
    card: <TypewriterCard />,
  },
  {
    label: 'Cadence',
    title: 'It Goes Out Whether You Remember Or Not.',
    body: 'A fixed weekly schedule across every channel, queued ahead and waiting on one thing, your approval. Customers start treating you as active instead of dormant.',
    card: <SchedulerCard />,
  },
];

export function SolutionSection() {
  const isMobile = useIsMobile();

  return (
    <section
      id="solution"
      style={{ padding: isMobile ? '60px 20px' : '130px 64px', background: 'rgba(6,6,6,0.88)' }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <FadeUp>
          <SolutionSpotlightCard />
        </FadeUp>

        <FadeUp delay={100}>
          <div style={{ marginBottom: 48 }}>
            <SectionH2 style={{ marginBottom: 16 }}>
              Content, lead response and reviews, run as one system you own.
            </SectionH2>
            <p style={{ color: TEXT_SECONDARY, fontSize: '1.05rem', lineHeight: 1.75, maxWidth: 680 }}>
              I scan what your competitors are getting engagement on, mine your own calls and inboxes
              for the objections that keep coming up, then write and publish against both. Every piece
              is human-checked before it goes live, and the files, the history and the reports are
              yours if you ever leave.
            </p>
          </div>
        </FadeUp>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: 20,
            marginBottom: 48,
          }}
        >
          {DEMO_CARDS.map((item, i) => (
            <FadeUp key={item.label} delay={i * 100}>
              <div
                style={{
                  background: SURFACE,
                  border: `1px solid ${BORDER}`,
                  borderRadius: 24,
                  padding: isMobile ? 24 : 32,
                  height: '100%',
                  transition: `border-color 0.3s, transform 0.3s ${EASE}`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255,107,0,0.25)';
                  e.currentTarget.style.transform = 'translateY(-3px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = BORDER;
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div
                  style={{
                    fontFamily: FONT_MONO,
                    fontSize: '0.62rem',
                    color: ACCENT,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    marginBottom: 14,
                  }}
                >
                  {item.label}
                </div>
                <h3
                  style={{
                    fontFamily: FONT_DISPLAY,
                    fontSize: '1.5rem',
                    fontWeight: 400,
                    textTransform: 'uppercase',
                    marginBottom: 8,
                    color: TEXT_PRIMARY,
                  }}
                >
                  {item.title}
                </h3>
                <p style={{ color: MUTED, fontSize: '0.85rem', lineHeight: 1.6, marginBottom: 20 }}>
                  {item.body}
                </p>
                {item.card}
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}
